import { useState, useEffect } from 'react'
import classnames from 'classnames'
import { FormField, Select } from '../../components/inputs'
import { Button } from '../../components/button'
import useAvailableMemberships from '../../hooks/use-available-memberhips'
import useAvailableGroups from '../../hooks/use-available-groups'
import compare from '../../utils/compare'

const validationErrors = {
  membership: 'Please select a Membership',
  group: 'Please select a Group',
  type: 'Please select a Type',
  memberID: 'Please enter a Member ID',
}

const generateMembershipObject = (membership, group, type, memberID) => {
  return {
    benefitsProgramId: membership,
    benefitsProgramGroupId: group,
    membershipType: type,
    membershipTypeDetails: memberID,
  }
}

export default function AddMembership({
  onSubmit,
  disabled,
  serverErrors,
  filteredMembership,
  color,
}) {
  const [mappedServerErrors, setMappedServerErrors] = useState(() =>
    mapServerErrors(serverErrors?.errors),
  )

  useEffect(() => setMappedServerErrors(mapServerErrors(serverErrors?.errors)), [serverErrors])

  const membershipTypes = [
    { value: 1, label: 'Member' },
    { value: 2, label: 'Friends and Family' },
  ]

  const { availableMemberships } = useAvailableMemberships([])
  const [membership, setMembership] = useState(null)
  const { availableGroups } = useAvailableGroups(membership?.value)
  const [groupValue, setGroupValue] = useState(null)
  const [type, setType] = useState(null)
  const [memberID, setMemberID] = useState('')
  const [validate, setValidate] = useState(false)

  const formattedMemberships = availableMemberships?.programs
    ?.filter((m) => m.benefitsProgramName === filteredMembership || !filteredMembership)
    .map((data) => {
      return { value: data.benefitsProgramId, label: data.benefitsProgramName }
    })
  const formattedGroups = availableGroups?.groups?.map((data) => {
    return { value: data.groupId, label: data.groupAbbreviation + ' ' + data.groupName }
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    setValidate(true)
    if (membership && groupValue && type && memberID) {
      setValidate(false)
      onSubmit(generateMembershipObject(membership.value, groupValue.value, type.value, memberID))
      if (!disabled) {
        setMembership(null)
        setGroupValue(null)
        setType(null)
        setMemberID('')
      }
    }
  }

  return (
    <form className='wrapper' onSubmit={handleSubmit}>
      <div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validate && !membership },
              { visible: validate && !membership },
            )}
            htmlFor='membership'
          >
            {validate && !membership && validationErrors.membership}
          </label>
          <Select
            placeholder='Select Membership'
            id='membership'
            name='membership'
            options={formattedMemberships?.sort(compare)}
            onChange={(e) => {
              setGroupValue(null)
              setMembership(e)
            }}
            value={membership}
            error={validate && !membership && validationErrors.membership}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validate && !groupValue },
              { visible: validate && !groupValue },
            )}
            htmlFor='membershipGroup'
          >
            {validate && !groupValue && validationErrors.group}
          </label>
          <Select
            placeholder='Select Group'
            id='membershipGroup'
            name='membershipGroup'
            options={formattedGroups?.sort(compare)}
            isDisabled={!membership}
            onChange={(e) => {
              setGroupValue(e)
            }}
            value={groupValue}
            error={validate && !groupValue && validationErrors.group}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validate && !type },
              { visible: validate && !type },
            )}
            htmlFor='type'
          >
            {validate && !type && validationErrors.type}
          </label>
          <Select
            placeholder='Select Type'
            id='type'
            name='type'
            options={membershipTypes}
            isDisabled={!membership}
            onChange={(value) => {
              setType(value)
            }}
            value={type}
            error={validate && !type && validationErrors.type}
          />
        </div>
        <div className='formFieldItem'>
          <label
            className={classnames(
              'label',
              { error: validate && !memberID },
              { visible: validate && !memberID },
            )}
            htmlFor='memberID'
          >
            {validate && !memberID && validationErrors.memberID}
          </label>
          <FormField
            placeholder='Member ID'
            type='text'
            id='memberID'
            name='memberID'
            value={memberID}
            error={validate && !memberID}
            onChange={(e) => setMemberID(e.target.value)}
          />
        </div>
      </div>
      <div className='flex flexDirectionColumn alignCenter mt-2'>
        <label
          className={classnames(
            'label',
            { error: mappedServerErrors.general },
            { visible: mappedServerErrors.general },
          )}
        >
          {mappedServerErrors.general}
        </label>

        <Button type='submit' color={color} className='flatbtn signup' disabled={disabled}>
          Sign Up
        </Button>
      </div>

      <style jsx>{`
        .wrapper {
          position: relative;
          width: 43.6rem;
          padding: 2.6rem 2rem 4.1rem;
          background: #fff;
        }

        .title {
          padding: 0 0 0.8rem;
          line-height: 3.6rem;
          font-size: 3.2rem;
        }

        .label {
          display: none;
          margin: 0 0 1rem;
          color: var(--secondary-text-color);
          font-size: 1.4rem;

          &.visible {
            display: inline-block;
          }

          &.error {
            color: var(--alert-color);
          }
        }

        .formFieldItem + .formFieldItem {
          padding: 1.7rem 0 0;
        }

        .formFieldItem {
          :global(#firstName, #lastName) {
            text-transform: capitalize;
          }
        }

        .mt-2 {
          padding-top: 2rem;
        }

        @media (max-width: 1024px) {
          .wrapper {
            width: 100%;
            padding: 1.6rem 2rem 4.1rem;
          }

          .title {
            padding: 0 0 0.5rem;
            line-height: 2.4rem;
            font-size: 1.8rem;
          }

          .loginInstead {
            padding: 0 0 1.6rem;
            line-height: 1.9rem;
            font-size: 1.4rem;
          }

          :global(.signup) {
            position: fixed;
            width: calc(100% - 2.8rem);
            bottom: 2.1rem;
          }
        }
      `}</style>
    </form>
  )
}

function mapServerErrors(errors) {
  if (!errors) return {}

  const mappedErrors = {}

  errors.forEach((error) => {
    mappedErrors['general'] = error.message
  })

  return mappedErrors
}
